import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslationApp } from "../../../lib/i18next";
import {
  MODAL_META_AD_LIBRARY_COMPETITOR_ALL,
  MODAL_META_AD_LIBRARY_PLATFORM_ALL,
} from "../../../i18n/keysTranslations";
import { startGetBrandInformation } from "../../../actions/adsCampaigns";
import { normalizeApiDataToForm } from "../../../pages/Settings/BrandSetup/utils/formDataTransformers";
import {
  startAddContentsToList,
  startGetCompetitorsAds,
  startPersistMetaAdLibraryImageToStorage,
} from "../../../actions/user";
import {
  filterGalleryItemsByCompetitor,
  filterGalleryItemsByPlatform,
  isMetaAdLibraryViewOnlyItem,
  normalizeCompetitorName,
} from "../../../services/competitorAdsGallery";
import { ALL_FILTER, GOOGLE, META } from "../../../utils/constants";
import { logoPlatform } from "../../../utils/string";
import {
  COMPETITOR_ADS_COMPETITOR_FILTER_FIELD,
  COMPETITOR_ADS_PLATFORM_FILTER_FIELD,
} from "./constants";

const COMPETITOR_ADS_PLATFORM_OPTIONS = [ALL_FILTER, META, GOOGLE];

export const useGalleryFromCompetitorAds = ({
  dispatch,
  pathStorageReferenceAd,
  businessCompetitors,
  setData,
  setError,
  setIsLoading,
  setIsUploading,
  setBusinessCompetitors,
  data,
}) => {
  const { t } = useTranslationApp();

  const {
    control,
    watch,
    setValue,
    reset: resetCompetitorFilterForm,
  } = useForm({
    defaultValues: {
      [COMPETITOR_ADS_PLATFORM_FILTER_FIELD]: ALL_FILTER,
      [COMPETITOR_ADS_COMPETITOR_FILTER_FIELD]: "",
    },
  });

  const selectedCompetitorFilter = watch(
    COMPETITOR_ADS_COMPETITOR_FILTER_FIELD
  );

  const selectedPlatform = watch(COMPETITOR_ADS_PLATFORM_FILTER_FIELD);

  const platformFilterOptions = useMemo(
    () =>
      COMPETITOR_ADS_PLATFORM_OPTIONS.map((platform) => ({
        value: platform,
        label:
          platform === ALL_FILTER
            ? t(MODAL_META_AD_LIBRARY_PLATFORM_ALL)
            : t(platform),
        iconSrc: platform === ALL_FILTER ? "" : logoPlatform(platform),
      })),
    [t]
  );

  const competitorFilterOptions = useMemo(() => {
    const allOption = {
      value: "",
      label: t(MODAL_META_AD_LIBRARY_COMPETITOR_ALL),
    };

    const competitorOptions = businessCompetitors.map((name) => ({
      value: normalizeCompetitorName(name),
      label: name.trim(),
    }));

    return [allOption, ...competitorOptions];
  }, [businessCompetitors, t]);

  const galleryDisplayData = useMemo(() => {
    const platformItems = filterGalleryItemsByPlatform({
      items: data,
      platformFilterValue: selectedPlatform,
    });

    return filterGalleryItemsByCompetitor({
      items: platformItems,
      competitorFilterValue: selectedCompetitorFilter,
    });
  }, [data, selectedCompetitorFilter, selectedPlatform]);

  const fetchCompetitorsAds = useCallback(async () => {
    setValue(COMPETITOR_ADS_COMPETITOR_FILTER_FIELD, "");

    setIsLoading(true);
    const competitorsResponse = await dispatch(
      startGetCompetitorsAds({ platform: ALL_FILTER })
    );

    if (competitorsResponse.ok) {
      setData(competitorsResponse.data || []);
      setError(null);
    } else {
      setError(competitorsResponse.message);
    }

    setIsLoading(false);
    return competitorsResponse;
  }, [dispatch, setData, setError, setIsLoading, setValue]);

  const onPlatformFilterChange = useCallback(() => {
    setValue(COMPETITOR_ADS_COMPETITOR_FILTER_FIELD, "");
  }, [setValue]);

  const onGetInitialData = useCallback(
    async ({ refreshAdsOnly = false } = {}) => {
      if (refreshAdsOnly) {
        return fetchCompetitorsAds();
      }

      setError(null);
      setData([]);
      setBusinessCompetitors([]);
      resetCompetitorFilterForm({
        [COMPETITOR_ADS_PLATFORM_FILTER_FIELD]: ALL_FILTER,
        [COMPETITOR_ADS_COMPETITOR_FILTER_FIELD]: "",
      });

      setIsLoading(true);
      const [competitorsResponse, brandResponse] = await Promise.all([
        dispatch(startGetCompetitorsAds({ platform: ALL_FILTER })),
        dispatch(startGetBrandInformation()),
      ]);

      if (brandResponse?.ok) {
        const normalized = normalizeApiDataToForm(brandResponse.data);
        setBusinessCompetitors(normalized.businessCompetitors || []);
      }

      if (competitorsResponse.ok) {
        setData(competitorsResponse.data || []);
      } else {
        setError(competitorsResponse.message);
      }
      setIsLoading(false);
      return competitorsResponse;
    },
    [
      dispatch,
      fetchCompetitorsAds,
      resetCompetitorFilterForm,
      setBusinessCompetitors,
      setData,
      setError,
      setIsLoading,
    ]
  );

  const onSendSelectedItems = useCallback(
    async (itemsToProcess, { onCallbackSelectedItems, onCloseModal }) => {
      const selectedItem = itemsToProcess[0];
      if (isMetaAdLibraryViewOnlyItem(selectedItem)) return;

      const dataUrl = selectedItem?.url;

      if (!dataUrl || !pathStorageReferenceAd) return;

      setIsUploading(true);

      const persistResponse = await dispatch(
        startPersistMetaAdLibraryImageToStorage({
          dataUrl,
          path: pathStorageReferenceAd,
        })
      );

      setIsUploading(false);

      if (!persistResponse?.ok || !persistResponse.url) {
        setError(
          persistResponse?.message || "The request could not be processed."
        );
        return;
      }

      const storedUrl = persistResponse.url;

      dispatch(startAddContentsToList([{ url: storedUrl, type: "upload" }]));

      onCallbackSelectedItems?.([
        {
          url: storedUrl,
          from: selectedItem.from,
          id: selectedItem.id,
          pageId: selectedItem.pageId,
          adId: selectedItem.adId,
        },
      ]);
      onCloseModal?.();
    },
    [dispatch, pathStorageReferenceAd, setError, setIsUploading]
  );

  return {
    control,
    competitorFilterOptions,
    galleryDisplayData,
    onGetInitialData,
    onPlatformFilterChange,
    onSendSelectedItems,
    platformFilterOptions,
    resetCompetitorFilterForm,
    selectedCompetitorFilter,
    selectedPlatform,
  };
};
